// src/utils/databaseUtils.js - UPDATED

/**
 * Checks database connection health
 * @returns {Promise<{success: boolean, data?: any, error?: Error}>} Connection status
 */
export const checkDatabaseConnection = async () => {
  try {
    console.log('Checking database connection...');

    // Increase timeout for slower connections
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    
    // Try the main health endpoint first
    try {
      const healthResponse = await fetch(`/api/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!healthResponse.ok) {
        console.warn(`Primary health check failed: ${healthResponse.status}`);
        throw new Error(`Health check failed: ${healthResponse.status}`);
      }

      const healthData = await healthResponse.json();
      console.log('Primary health check response:', healthData);

      // Check if database is actually connected
      if (healthData.database === 'connected') {
        console.log('Database confirmed connected');
        return {
          success: true,
          online: true,
          message: 'Database connection confirmed',
          healthData
        };
      } else {
        console.warn('Primary health endpoint working but database not connected');
        throw new Error('Database not connected');
      }
    } catch (primaryError) {
      console.warn('Primary health check failed, trying simple-health:', primaryError);
      
      // CRITICAL CHANGE: Even if simple-health succeeds, we know the database is not available
      try {
        const simpleResponse = await fetch(`/api/simple-health`, {
          headers: { 'Cache-Control': 'no-cache' },
          signal: AbortSignal.timeout(5000)
        });
        
        if (simpleResponse.ok) {
          const simpleData = await simpleResponse.json();
          console.log('Simple health check succeeded. API is available:', simpleData);
          
          // Even if the API is available, we'll still use localStorage mode
          // since the database connection failed
          window.usingLocalStorageOnly = true;
          
          return {
            success: false,  // Mark as unsuccessful for database connections
            apiAvailable: true,
            message: 'API is working but database is unavailable',
            simpleData
          };
        }
      } catch (simpleError) {
        console.warn('Both health checks failed:', simpleError);
      }
      
      window.usingLocalStorageOnly = true;
      return {
        success: false,
        online: false,
        message: 'All health checks failed',
        error: primaryError
      };
    }
  } catch (error) {
    console.warn('Database check failed:', error);
    window.usingLocalStorageOnly = true;
    return {
      success: false,
      online: false,
      message: error.message,
      error: error
    };
  }
};

export const initializeClientDatabase = async () => {
  try {
    console.log('Starting database initialization...');
    
    // Make sure localStorage is available as fallback
    if (typeof localStorage === 'undefined') {
      console.error('localStorage is not available');
      window.usingLocalStorageOnly = true;
      return false;
    }
    
    // Initialize quiz_results storage (always do this for fallback)
    if (!localStorage.getItem('quiz_results')) {
      console.log('Creating quiz_results storage');
      localStorage.setItem('quiz_results', JSON.stringify([]));
    } else {
      // Validate existing data
      try {
        const existingData = JSON.parse(localStorage.getItem('quiz_results'));
        if (!Array.isArray(existingData)) {
          console.warn('quiz_results is not an array, resetting');
          localStorage.setItem('quiz_results', JSON.stringify([]));
        }
      } catch (e) {
        console.error('Invalid quiz_results data, resetting', e);
        localStorage.setItem('quiz_results', JSON.stringify([]));
      }
    }
    
    // Reset localStorage flag at startup
    window.usingLocalStorageOnly = false;
    
    // Skip database initialization if:
    // - Forced to use localStorage via localStorage setting
    // - Currently offline
    if (localStorage.getItem('forceLocalStorage') === 'true' || !navigator.onLine) {
      console.log('Using localStorage due to:', 
        localStorage.getItem('forceLocalStorage') === 'true' ? 'forced setting' : 'offline status');
      window.usingLocalStorageOnly = true;
      return { success: true, mode: 'localStorage' };
    }
    
    // Try API call to init-db endpoint
    try {
      console.log('Initializing database via API call...');
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8-second timeout
      
      const response = await fetch('/api/init-db', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.warn('Database init endpoint returned error:', response.status);
        throw new Error(`Database init failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Database init response:', data);
      
      // Check if we got a valid response indicating DB success
      if (data.success) {
        console.log('Database initialized successfully via API');
        window.usingLocalStorageOnly = false;
        return { success: true, mode: 'database', data };
      } else {
        console.warn('Database init response indicates failure', data);
        window.usingLocalStorageOnly = true;
        return { success: false, mode: 'localStorage', reason: 'api-error' };
      }
    } catch (apiError) {
      console.warn('Database API unavailable, using localStorage:', apiError.message);
      window.usingLocalStorageOnly = true;
      return { success: false, mode: 'localStorage', reason: 'api-error', error: apiError };
    }
  } catch (error) {
    console.warn('Database initialization error:', error);
    window.usingLocalStorageOnly = true;
    return { success: false, mode: 'localStorage', reason: 'general-error', error };
  }
};

// Add this to src/utils/databaseUtils.js
export const testDatabaseConnection = async () => {
  console.log('Testing database connection...');
  
  try {
    // Test three endpoints to diagnose issues
    const endpoints = [
      '/api/health', 
      '/api/simple-health', 
      '/api/init-db'
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`Testing ${endpoint}...`);
        const response = await fetch(endpoint, {
          headers: { 'Cache-Control': 'no-cache' },
          signal: AbortSignal.timeout(5000)
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log(`✓ ${endpoint} succeeded:`, data);
        } else {
          console.log(`✗ ${endpoint} failed: ${response.status}`);
        }
      } catch (e) {
        console.log(`✗ ${endpoint} error:`, e.message);
      }
    }
    
    // Test submit endpoint with minimal payload
    try {
      console.log('Testing submit endpoint...');
      const testData = {
        user_id: 'test_user',
        quiz_name: 'Database Test',
        score: 100,
        total_questions: 1,
        answers: { test: true }
      };
      
      const response = await fetch('/api/submit-quiz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify(testData),
        signal: AbortSignal.timeout(5000)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✓ Submit test succeeded:', data);
        return { success: true, allPassed: true };
      } else {
        console.log('✗ Submit test failed:', response.status);
        return { success: false, submitFailed: true };
      }
    } catch (e) {
      console.log('✗ Submit test error:', e.message);
      return { success: false, submitError: e.message };
    }
  } catch (error) {
    console.error('Test failed:', error);
    return { success: false, error: error.message };
  }
};